import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getEncounter } from '../api/encounters.js'
import { listEncounterDocuments, totalDocumentCount } from '../api/documents.js'
import DocumentLink from '../components/DocumentLink.jsx'
import { formatMrn } from '../api/patients.js'
import { formatDate, formatDateTime } from '../utils/format.js'

/**
 * Standalone printable / save-as-PDF view of a single encounter.
 *
 * Renders only sections and fields that contain data — empty headings
 * would read like missing documentation in a clinical record. The output
 * mirrors a paper discharge summary: each heading is present *because*
 * the clinician recorded something, not as a placeholder.
 *
 * Print stylesheet (`@media print`):
 *   * drops the on-screen toolbar
 *   * sets A4 page size + sensible margins
 *   * forces page-break-inside: avoid on each section + per-order card
 *   * white backgrounds (no coloured shading) for ink-friendly output
 */
export default function PrintEncounter() {
  const { encounterId } = useParams()
  const nav = useNavigate()

  const q = useQuery({
    queryKey: ['encounter', encounterId],
    queryFn: () => getEncounter(encounterId),
    enabled: Boolean(encounterId),
  })

  // Pull every supporting document attached to this encounter chain
  // (encounter row, patient profile, each lab order, each imaging order).
  // Runs in parallel with the encounter fetch so the print page is
  // ready in one render once both resolve.
  const docsQ = useQuery({
    queryKey: ['encounter-documents', q.data?.id, q.data?.lab_orders?.length, q.data?.imaging_orders?.length],
    queryFn: () => listEncounterDocuments(q.data),
    enabled: Boolean(q.data?.id),
    staleTime: 30_000,
  })

  useEffect(() => { window.scrollTo(0, 0) }, [])

  // Where to send the user when they click "Back". Prefer an explicit
  // route over `nav(-1)` because the print page is often opened in a
  // new tab or refreshed, leaving an empty history stack — `-1` then
  // becomes a no-op and the user feels stuck.
  const backTo = encounterId ? `/doctor/encounters/${encounterId}` : '/doctor'

  if (q.isLoading) return <Loading />
  if (q.isError || !q.data) return <Error onBack={() => nav(backTo)} />

  const enc = q.data
  const docs = docsQ.data || { encounter: [], patient: [], labs: [], imaging: [] }
  const docCount = totalDocumentCount(docs)
  const note = enc.structured?.clinical_note || {}
  const vitals = enc.structured?.vitals || {}
  const codes = enc.icd10_codes || []
  const dxByType = {
    provisional: codes.filter((c) => (c._diagnosisType || '') === 'provisional'),
    differential: codes.filter((c) => (c._diagnosisType || '') === 'differential'),
    final: codes.filter((c) => (c._diagnosisType || '') === 'final'),
    other: codes.filter((c) => !['provisional', 'differential', 'final'].includes(c._diagnosisType || '')),
  }

  const mrn = enc.patient ? formatMrn(enc.patient) : ''
  const ageText = computeAgeText(enc.patient_dob)

  // Pre-compute which sections have content. Anything that resolves to
  // `false` here is omitted from the printed output entirely — heading
  // and all. This keeps the discharge summary focused on what the
  // clinician actually recorded.
  const filledVitals = Object.entries(vitals).filter(
    ([k, v]) => v != null && v !== '' && k !== '_vitalId' && k !== 'recorded_at',
  )
  // HPI source priority:
  //   1. The narrative's intro paragraph (text BEFORE the first
  //      structured heading), if the extractor identified one.
  //   2. The extracted structured `history_of_presenting_illness` field.
  //   3. As a last resort, the raw narrative blob — but ONLY when the
  //      doctor wrote nothing structured at all.
  // We deliberately do NOT prefer `enc.narrative` over the structured
  // HPI: if both exist, rendering both duplicates the same content (the
  // narrative is just the source the structured field was extracted
  // from). This was the bug behind the doubled AMPLE History display.
  const hasAnyStructured = Boolean(
    note.history_of_presenting_illness
    || note.ample_allergies || note.ample_medications
    || note.ample_past_medical_history || note.ample_last_meal || note.ample_events
    || note.review_of_systems || note.physical_examination
    || note.treatment_plan || note.follow_up_plan
  )
  const hpiText =
    note.narrative_intro
    || note.history_of_presenting_illness
    || (hasAnyStructured ? '' : enc.narrative)
  const has = {
    identity: hasContent(enc.patient_name) || hasContent(mrn) || hasContent(enc.patient_phone)
      || hasContent(enc.patient_sex) || hasContent(enc.patient_dob),
    chief: hasContent(enc.chief_complaint),
    hpi: hasContent(hpiText),
    ample: hasContent(note.ample_allergies) || hasContent(note.ample_medications)
      || hasContent(note.ample_past_medical_history) || hasContent(note.ample_last_meal)
      || hasContent(note.ample_events),
    ros: hasContent(note.review_of_systems),
    vitals: filledVitals.length > 0,
    pe: hasContent(note.physical_examination),
    provisional: hasContent(note.provisional_diagnosis) || dxByType.provisional.length > 0,
    differential: hasContent(note.differential_diagnosis) || dxByType.differential.length > 0,
    // Empirical treatment — what was started before final dx confirmed.
    // Sits between differential and final in the rendered order.
    empirical: hasContent(note.empirical_treatment),
    final: dxByType.final.length > 0,
    otherDx: dxByType.other.length > 0,
    treatment: hasContent(note.treatment_plan),
    rx: (enc.prescriptions || []).length > 0,
    labs: (enc.lab_orders || []).length > 0,
    imaging: (enc.imaging_orders || []).length > 0,
    referrals: (enc.referrals || []).length > 0,
    ambulance: (enc.ambulance_requests || []).length > 0,
    followUp: hasContent(note.follow_up_plan),
    addendums: (enc.addendums || []).length > 0,
    docs: docCount > 0,
  }

  // Title reflects the clinical state of the document — a submitted
  // encounter is essentially a discharge summary; a draft is a working
  // record.
  const docTitle = enc.status === 'submitted'
    ? 'Discharge Summary'
    : 'Clinical Encounter Summary'

  return (
    <div className="print-page">
      <PrintStyles />

      {/*
        z-50 so this toolbar (and its dropdown) layer above the
        document body. The .page-body content lives inside its own
        stacking context (z-1, set up so the watermark image stays
        underneath), and z-30 on the dropdown wasn't enough to escape
        it. z-50 puts the toolbar reliably on top.
      */}
      <div className="no-print sticky top-0 z-50 bg-slate-50 border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <button onClick={() => nav(backTo)} className="text-sm text-slate-600 underline">← Back to encounter</button>
        {/*
          Print/Save/Email dropdown — matches the dropdown used on every
          other generated document so the doctor's muscle memory is the
          same. Print + Save use the browser's print dialog (it has a
          "Save as PDF" destination); Email points to the Generate
          Documents flow on the encounter where a real PDF can be
          rendered + attached + emailed in one click.
        */}
        <PrintActions encounterId={encounterId} backTo={backTo} nav={nav} />
      </div>

      <div className="page-body">
        {/* Watermark — sits absolutely positioned behind everything,
            see .page-watermark CSS above. Hidden if the asset isn't
            yet on disk. */}
        <img
          className="page-watermark"
          src="/branding/icer-health-watermark.png"
          alt=""
          onError={(e) => { e.currentTarget.style.display = 'none' }}
        />
        {/* Letterhead */}
        <header className="letterhead">
          <div className="brand">
            {/* ICER Health logo on a white badge. The badge keeps the
                colour logo sharp on any background and gives the
                letterhead the same visual weight as the FE app header. */}
            <div className="brand-badge">
              <img
                className="brand-mark"
                src="/branding/icer-logo.png"
                alt="ICER Health"
                onError={(e) => { e.currentTarget.style.display = 'none' }}
              />
            </div>
            <div>
              <div className="brand-name">Wantok Telehealth</div>
              <div className="brand-sub">ICER Health · Papua New Guinea</div>
            </div>
          </div>
          <div className="doc-meta">
            <div className="doc-title">{docTitle}</div>
            <div className="doc-date">Generated {formatDateTime(new Date().toISOString())}</div>
          </div>
        </header>

        <hr className="rule" />

        {has.identity && (
          <Section title="Patient identity">
            <KV label="Patient" value={enc.patient_name} bold />
            <KV label="MRN" value={mrn} mono />
            <KV
              label="Sex / DOB / Age"
              value={[
                enc.patient_sex && capitalize(enc.patient_sex),
                enc.patient_dob && `DOB ${enc.patient_dob}`,
                ageText,
              ].filter(Boolean).join(' · ')}
            />
            <KV label="Contact" value={enc.patient_phone} />
          </Section>
        )}

        <Section title="Encounter">
          <KV label="Encounter ID" value={`#${enc.id}`} mono />
          <KV label="Status" value={(enc.status || '').toUpperCase()} />
          <KV label="Type" value={enc.consultation_type} />
          <KV
            label="Encounter date"
            value={
              enc.encounter_date || enc.created_at
                ? formatDateTime(enc.encounter_date || enc.created_at)
                : ''
            }
          />
          {enc.submitted_at && <KV label="Submitted" value={formatDateTime(enc.submitted_at)} />}
          <KV label="Attending clinician" value={enc.doctor_name ? `Dr ${enc.doctor_name}` : ''} />
        </Section>

        {has.chief && (
          <Section title="Chief complaint">
            <Para value={enc.chief_complaint} />
          </Section>
        )}

        {has.hpi && (
          <Section title="History of presenting illness">
            <Para value={hpiText} />
          </Section>
        )}

        {has.ample && (
          <Section title="AMPLE history">
            <KV label="Allergies" value={note.ample_allergies} />
            <KV label="Medications" value={note.ample_medications} />
            <KV label="Past medical history" value={note.ample_past_medical_history} />
            <KV label="Last oral intake" value={note.ample_last_meal} />
            <KV label="Events leading to presentation" value={note.ample_events} />
          </Section>
        )}

        {has.ros && (
          <Section title="Review of systems">
            <Para value={note.review_of_systems} />
          </Section>
        )}

        {has.vitals && (
          <Section title="Vitals (Objective)">
            <VitalsTable filled={filledVitals} />
          </Section>
        )}

        {has.pe && (
          <Section title="Physical examination">
            <Para value={note.physical_examination} />
          </Section>
        )}

        {has.provisional && (
          <Section title="Provisional diagnosis">
            <Para value={note.provisional_diagnosis} />
            {dxByType.provisional.length > 0 && <DxList codes={dxByType.provisional} />}
          </Section>
        )}

        {has.differential && (
          <Section title="Differential diagnoses">
            <Para value={note.differential_diagnosis} />
            {dxByType.differential.length > 0 && <DxList codes={dxByType.differential} />}
          </Section>
        )}

        {has.empirical && (
          <Section title="Empirical treatment">
            <Para value={note.empirical_treatment} />
          </Section>
        )}

        {has.final && (
          <Section title="Final diagnoses">
            <DxList codes={dxByType.final} />
          </Section>
        )}

        {has.otherDx && (
          <Section title="Other coded diagnoses">
            <DxList codes={dxByType.other} />
          </Section>
        )}

        {has.treatment && (
          <Section title="Treatment plan">
            <Para value={note.treatment_plan} />
          </Section>
        )}

        {has.rx && (
          <Section title="Prescriptions">
            {enc.prescriptions.map((rx) => <RxBlock key={rx.id} rx={rx} />)}
          </Section>
        )}

        {has.labs && (
          <Section title="Lab orders">
            {enc.lab_orders.map((lo) => <LabBlock key={lo.id} lo={lo} />)}
          </Section>
        )}

        {has.imaging && (
          <Section title="Imaging orders">
            {enc.imaging_orders.map((io) => <ImagingBlock key={io.id} io={io} />)}
          </Section>
        )}

        {has.referrals && (
          <Section title="Referrals">
            {enc.referrals.map((r) => <ReferralBlock key={r.id} r={r} />)}
          </Section>
        )}

        {has.ambulance && (
          <Section title="Ambulance requests">
            {enc.ambulance_requests.map((a) => <AmbulanceBlock key={a.id} a={a} />)}
          </Section>
        )}

        {has.followUp && (
          <Section title="Follow-up plan">
            <Para value={note.follow_up_plan} />
          </Section>
        )}

        {has.addendums && (
          <Section title="Addendums">
            {enc.addendums.map((a, i) => (
              <div key={i} className="card">
                <div className="card-head">{a.author || 'Provider'} · {formatDateTime(a.created_at)}</div>
                <pre className="narrative">{a.text}</pre>
              </div>
            ))}
          </Section>
        )}

        {has.docs && (
          <Section title={`Supporting documents (${docCount})`}>
            {(docs.patient || []).length > 0 && (
              <DocBucket
                heading="Patient profile attachments"
                hint="ID, insurance, prior records uploaded against the patient profile."
                docs={docs.patient}
              />
            )}
            {(docs.encounter || []).length > 0 && (
              <DocBucket
                heading="Encounter attachments"
                hint="Files uploaded directly against this encounter."
                docs={docs.encounter}
              />
            )}
            {(docs.labs || []).map((g) => (
              <DocBucket
                key={`lab-${g.orderId}`}
                heading={`Lab #${g.orderId} — ${g.label}`}
                hint="Result files uploaded by the laboratory."
                docs={g.docs}
              />
            ))}
            {(docs.imaging || []).map((g) => (
              <DocBucket
                key={`img-${g.orderId}`}
                heading={`Imaging #${g.orderId} — ${g.label}`}
                hint="Reports and imaging files uploaded by radiology."
                docs={g.docs}
              />
            ))}
            <p className="empty" style={{ marginTop: 8 }}>
              Each link above opens the original file. Files referenced in this document are part of the
              clinical record and must be retained alongside it.
            </p>
          </Section>
        )}

        <section className="signature">
          <div className="sig-line" />
          <div className="sig-meta">
            {enc.doctor_name ? `Dr ${enc.doctor_name}` : 'Attending clinician'}
            {enc.submitted_at && (<span> · Submitted {formatDate(enc.submitted_at)}</span>)}
          </div>
        </section>

        <hr className="rule" />

        <footer>
          <p><strong>Confidential clinical record.</strong> This document contains protected health information.
            Disclose only to persons authorised by the patient or by law.</p>
          <p className="muted">
            Wantok Telehealth · ICER Health · Data residency: Papua New Guinea ·
            Encounter #{enc.id} · MRN {mrn || '—'}
          </p>
        </footer>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Loading() {
  return <div className="p-12 text-center text-slate-400 text-sm">Loading encounter…</div>
}

function Error({ onBack }) {
  return (
    <div className="p-12 text-center">
      <p className="text-rose-700 mb-3">Couldn't load this encounter.</p>
      <button onClick={onBack} className="text-sm text-wantok-700 underline">Go back</button>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <section className="doc-section">
      <h2 className="section-title">{title}</h2>
      <div>{children}</div>
    </section>
  )
}

/**
 * Label/value row. Returns null when the value is empty so the printed
 * record never shows "Not recorded" placeholders — a healthcare document
 * lists what *was* documented, not what wasn't.
 */
function KV({ label, value, bold, mono }) {
  if (!hasContent(value)) return null
  return (
    <div className="kv">
      <div className="kv-label">{label}</div>
      <div className={'kv-value' + (bold ? ' bold' : '') + (mono ? ' mono' : '')}>
        {value}
      </div>
    </div>
  )
}

/**
 * Free-text paragraph block (HPI, ROS, treatment plan, …).
 * Returns null when empty — see KV.
 */
function Para({ value }) {
  if (!hasContent(value)) return null
  return <pre className="narrative">{value}</pre>
}

function VitalsTable({ filled }) {
  if (!filled || filled.length === 0) return null
  const labels = {
    temperature_c: 'Temp °C',
    heart_rate: 'HR bpm',
    resp_rate: 'RR bpm',
    bp_systolic: 'BP sys mmHg',
    bp_diastolic: 'BP dia mmHg',
    spo2: 'SpO₂ %',
    weight_kg: 'Weight kg',
    height_cm: 'Height cm',
  }
  return (
    <table className="data-table">
      <thead><tr>{filled.map(([k]) => <th key={k}>{labels[k] || k}</th>)}</tr></thead>
      <tbody><tr>{filled.map(([k, v]) => <td key={k}>{v}</td>)}</tr></tbody>
    </table>
  )
}

function DxList({ codes }) {
  return (
    <ul className="dx-list">
      {codes.map((c) => (
        <li key={c.code}>
          <span className="code">{c.code}</span>
          <span>{c.description}</span>
        </li>
      ))}
    </ul>
  )
}

function RxBlock({ rx }) {
  return (
    <div className="card">
      <div className="card-head">
        Rx #{rx.id} · status {rx.status}
        {rx.pharmacy_name && <> · → {rx.pharmacy_name}</>}
      </div>
      {(rx.items || []).length > 0 ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Drug</th><th>Strength</th><th>Route</th>
              <th>Dose</th><th>Frequency</th><th>Duration</th><th>Qty</th>
            </tr>
          </thead>
          <tbody>
            {rx.items.map((it) => (
              <tr key={it.id || it.drug_code}>
                <td>{it.drug_name}</td>
                <td>{it.strength || '—'}</td>
                <td>{it.route || '—'}</td>
                <td>{it.dose || '—'}</td>
                <td>{it.frequency || '—'}</td>
                <td>{it.duration || '—'}</td>
                <td>{it.quantity || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : rx.medication_name ? (
        <p>{rx.medication_name} {rx.dosage} {rx.frequency} {rx.duration}</p>
      ) : null}
      {rx.notes && <p className="card-note">Note: {rx.notes}</p>}
    </div>
  )
}

function LabBlock({ lo }) {
  const tests = (lo.items || []).map((it) => it.test_name).filter(Boolean).join(', ')
  return (
    <div className="card">
      <div className="card-head">
        Lab #{lo.id} · status {lo.status}
        {lo.laboratory_name && <> · → {lo.laboratory_name}</>}
      </div>
      {tests && <p>{tests}</p>}
      {lo.clinical_notes && <p className="card-note">Indication: {lo.clinical_notes}</p>}
      {lo.result_summary && (
        <div className="result"><strong>Result:</strong> {lo.result_summary}</div>
      )}
    </div>
  )
}

function ImagingBlock({ io }) {
  const studies = (io.items || []).map((it) => it.study_name).filter(Boolean).join(', ')
  const studyLine = studies || io.imaging_type
  return (
    <div className="card">
      <div className="card-head">
        Imaging #{io.id} · status {io.status}
        {io.radiology_center_name && <> · → {io.radiology_center_name}</>}
      </div>
      {studyLine && <p>{studyLine}</p>}
      {io.clinical_indication && <p className="card-note">Indication: {io.clinical_indication}</p>}
      {io.report_summary && (
        <div className="result"><strong>Report:</strong> {io.report_summary}</div>
      )}
    </div>
  )
}

function ReferralBlock({ r }) {
  const fromVal = [r.from_facility_type_display, r.from_cadre_display, r.from_specialty].filter(Boolean).join(' · ')
  const toVal = [r.to_facility_type_display, r.to_cadre_display, r.specialty, r.to_facility_name].filter(Boolean).join(' · ')
  return (
    <div className="card">
      <div className="card-head">
        Referral #{r.id} · status {r.status}
        {r.urgency && <> · {r.urgency}</>}
      </div>
      <div className="grid two-col tight">
        <KV label="From" value={fromVal} />
        <KV label="To" value={toVal} />
      </div>
      {r.reason && <p className="card-note">Reason: {r.reason}</p>}
    </div>
  )
}

function AmbulanceBlock({ a }) {
  const route = [a.pickup_location, a.destination].filter(Boolean).join(' → ')
  return (
    <div className="card">
      <div className="card-head">
        Ambulance #{a.id} · status {a.status}
        {a.urgency && <> · {a.urgency}</>}
        {a.ambulance_service_name && <> · → {a.ambulance_service_name}</>}
      </div>
      {route && <p>{route}</p>}
      {a.clinical_summary && <p className="card-note">{a.clinical_summary}</p>}
    </div>
  )
}

/**
 * One titled bucket of supporting documents (patient profile, encounter,
 * a lab order, or an imaging order). Each row is one file row + the
 * full URL printed in monospace below — so the URL is captured in the
 * PDF even after the document is printed.
 */
function DocBucket({ heading, hint, docs }) {
  return (
    <div className="appendix-bucket">
      <div className="appendix-heading">{heading}</div>
      {hint && <div className="appendix-hint">{hint}</div>}
      <table className="data-table">
        <thead>
          <tr>
            <th>File</th>
            <th>Kind</th>
            <th>Size</th>
            <th>Uploaded</th>
            <th>Open</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((d) => (
            <tr key={d.id}>
              <td>
                <div>{d.original_filename || `Document #${d.id}`}</div>
                {d.description && <div className="appendix-desc">{d.description}</div>}
                {d.file_url && <div className="appendix-url">{d.file_url}</div>}
              </td>
              <td>{d.kind || 'document'}</td>
              <td>{formatBytes(d.size_bytes)}</td>
              <td>
                <div>{d.uploaded_at ? formatDate(d.uploaded_at) : '—'}</div>
                {d.uploaded_by_username && (
                  <div className="appendix-meta">by {d.uploaded_by_username}</div>
                )}
              </td>
              <td>
                {d.id ? <DocumentLink doc={d} /> : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function formatBytes(n) {
  if (n == null) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Treat a value as "content" only if it is genuinely populated.
 *
 * IMPORTANT — clinical semantics:
 *   "Nil", "None", "Negative", "WNL", "NAD", "0" are all VALID clinical
 *   entries (e.g. Allergies: Nil, ROS: Nil, Pain score: 0). They are
 *   *not* shorthand for "missing" — the clinician deliberately recorded
 *   the absence of a finding. We render them verbatim. Only null /
 *   undefined / whitespace-only strings count as missing.
 */
function hasContent(v) {
  if (v == null) return false
  if (typeof v === 'string') return v.trim().length > 0
  return true
}

function computeAgeText(dob) {
  if (!dob) return ''
  const d = new Date(dob)
  if (isNaN(d.getTime())) return ''
  const now = new Date()
  let a = now.getFullYear() - d.getFullYear()
  const m = now.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1
  return `${a} years`
}

function capitalize(s) {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ---------------------------------------------------------------------------
// Embedded print stylesheet
// ---------------------------------------------------------------------------

function PrintStyles() {
  return (
    <style>{`
      .print-page { background: #f8fafc; min-height: 100vh; }
      .page-body {
        position: relative;
        max-width: 210mm; margin: 0 auto; padding: 16mm 18mm; background: white;
        color: #1f2937; font-family: -apple-system, "Segoe UI", Arial, sans-serif;
        font-size: 11pt; line-height: 1.45;
      }
      /* Watermark — matched to the PDF tuning so the on-screen
         preview reflects exactly what comes out on paper. 3% on
         a pure-white printed page is the printing-industry sweet
         spot for "stamp behind content" without competing with text.
         Grayscale + slight darken makes it read as embossed ink
         rather than translucent colour. */
      .page-watermark {
        position: absolute;
        top: 50%; left: 50%;
        width: 60%; max-width: 380px;
        transform: translate(-50%, -50%) rotate(-10deg);
        opacity: 0.04;
        filter: grayscale(1) brightness(0.5);
        pointer-events: none;
        user-select: none;
        z-index: 0;
      }
      .page-body > *:not(.page-watermark) { position: relative; z-index: 1; }
      .letterhead { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
      .brand { display: flex; gap: 12px; align-items: center; }
      /* White circular badge — matches the FE app-header treatment so
         the printed letterhead reads as one with the on-screen brand. */
      .brand-badge {
        width: 56px; height: 56px; border-radius: 50%; background: white;
        box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
        display: inline-flex; align-items: center; justify-content: center;
      }
      .brand-mark { width: 50px; height: 50px; object-fit: contain; display: block; }
      .brand-name { font-weight: 700; font-size: 16pt; color: #0f766e; line-height: 1.1; }
      .brand-sub { font-size: 9pt; color: #6b7280; }
      .doc-meta { text-align: right; }
      .doc-title { font-weight: 700; font-size: 13pt; }
      .doc-date { font-size: 9pt; color: #6b7280; }
      .rule { border: none; border-top: 2px solid #0f766e; margin: 12px 0 18px; }

      .doc-section { margin: 12px 0; page-break-inside: avoid; }
      .section-title {
        font-size: 11pt; font-weight: 700; color: #0f766e;
        text-transform: uppercase; letter-spacing: 0.05em;
        margin: 0 0 6px;
        border-bottom: 1px solid #e5e7eb; padding-bottom: 3px;
      }

      .kv { display: flex; gap: 12px; align-items: baseline; margin-bottom: 4px; }
      .kv-label {
        font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.05em;
        color: #6b7280; min-width: 180px;
      }
      .kv-value { font-size: 11pt; word-break: break-word; }
      .kv-value.bold { font-weight: 700; }
      .kv-value.mono { font-family: ui-monospace, "SF Mono", Consolas, monospace; }

      .narrative {
        white-space: pre-wrap; font-family: -apple-system, "Segoe UI", Arial, sans-serif;
        font-size: 11pt; line-height: 1.55; background: #f9fafb;
        border-left: 3px solid #0f766e; padding: 6px 10px; margin: 0;
      }

      .empty { color: #9ca3af; font-style: italic; font-size: 10pt; margin: 2px 0; }

      .grid { display: grid; gap: 4px 24px; }
      .grid.two-col { grid-template-columns: 1fr 1fr; }
      .grid.tight { gap: 2px 24px; }

      .dx-list { list-style: none; padding: 0; margin: 4px 0; }
      .dx-list li {
        padding: 3px 0; border-bottom: 1px dotted #e5e7eb;
        display: flex; gap: 12px;
      }
      .dx-list .code {
        font-family: ui-monospace, "SF Mono", Consolas, monospace;
        font-weight: 700; color: #0f766e; min-width: 64px;
      }

      .card {
        border: 1px solid #e5e7eb; border-radius: 6px;
        padding: 8px 12px; margin: 6px 0; background: #fafafa;
        page-break-inside: avoid;
      }
      .card-head {
        font-size: 9pt; color: #6b7280; margin-bottom: 4px;
        text-transform: uppercase; letter-spacing: 0.04em;
      }
      .card-note { font-size: 10pt; color: #4b5563; margin: 4px 0 0; font-style: italic; }
      .result {
        background: #ecfdf5; border-left: 3px solid #059669;
        padding: 6px 10px; margin-top: 6px; font-size: 10pt;
      }

      .data-table { width: 100%; border-collapse: collapse; margin: 4px 0; font-size: 10pt; }
      .data-table th, .data-table td {
        text-align: left; padding: 4px 8px; border-bottom: 1px solid #e5e7eb;
      }
      .data-table th { background: #f3f4f6; font-weight: 600; font-size: 9pt; text-transform: uppercase; }

      .signature { margin-top: 32px; page-break-inside: avoid; }
      .sig-line { border-bottom: 1px solid #1f2937; width: 60%; height: 20px; }
      .sig-meta { font-size: 9pt; color: #6b7280; margin-top: 4px; }

      .appendix-bucket { margin: 8px 0 12px; page-break-inside: avoid; }
      .appendix-heading {
        font-size: 10pt; font-weight: 700; color: #1f2937;
        margin-bottom: 2px;
      }
      .appendix-hint { font-size: 9pt; color: #6b7280; margin-bottom: 6px; font-style: italic; }
      .appendix-desc { font-size: 9pt; color: #4b5563; font-style: italic; margin-top: 2px; }
      .appendix-url {
        font-family: ui-monospace, "SF Mono", Consolas, monospace;
        font-size: 8.5pt; color: #4b5563; margin-top: 2px; word-break: break-all;
      }
      .appendix-meta { font-size: 8.5pt; color: #6b7280; }

      footer { margin-top: 18px; font-size: 8.5pt; color: #6b7280; text-align: center; }
      footer p { margin: 4px 0; }
      footer strong { color: #1f2937; }

      @media print {
        @page { size: A4; margin: 12mm 14mm; }
        .no-print { display: none !important; }
        .print-page { background: white; }
        .page-body { padding: 0; max-width: none; box-shadow: none; }
        .doc-section, .card, .signature { page-break-inside: avoid; }
        .card { background: white; }
        .narrative { background: white; }
        .result { background: white; }
        .data-table th { background: white; }
      }
    `}</style>
  )
}

// ---------------------------------------------------------------------------
// PrintActions — dropdown that mirrors the Generate Documents flow.
// Print uses window.print() (browser dialog handles save-as-PDF too);
// Email points the doctor at the Generate Documents → Medical Report
// → Email flow which renders a real PDF and emails it as an attachment.
// ---------------------------------------------------------------------------

function PrintActions({ encounterId, backTo, nav }) {
  const [open, setOpen] = useState(false)
  const [emailHint, setEmailHint] = useState(false)
  const wrapperRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const handlePrint = () => { setOpen(false); window.print() }
  const handleSave = () => {
    setOpen(false)
    // Browser print dialog has a "Save as PDF" destination — that's
    // the canonical way to save a printable HTML page to disk.
    window.print()
  }
  const handleEmail = () => {
    setOpen(false)
    setEmailHint(true)
    setTimeout(() => setEmailHint(false), 6000)
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-sm font-bold bg-wantok-700 hover:bg-wantok-800 text-white rounded-lg px-4 py-2 inline-flex items-center gap-2 shadow-sm"
      >
        <span>📄</span>
        <span>Print · Save · Email</span>
        <span className={'transition ' + (open ? 'rotate-180' : '')}>▾</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 bg-white border-2 border-slate-200 rounded-lg shadow-2xl z-50 overflow-hidden">
          <ActionItem icon="🖨️" label="Print" hint="Send to your printer" onClick={handlePrint} />
          <ActionItem icon="💾" label="Save as PDF" hint="Pick 'Save as PDF' as destination" onClick={handleSave} />
          <ActionItem icon="✉️" label="Email" hint="Send as a PDF attachment" onClick={handleEmail} />
        </div>
      )}

      {emailHint && (
        <div className="absolute right-0 mt-2 w-80 bg-amber-50 border-2 border-amber-200 text-amber-900 rounded-lg shadow-lg p-3 text-xs z-30">
          <div className="font-bold mb-1">To email this summary as a PDF attachment:</div>
          <div>
            Go back to the encounter and use the <b>Generate Documents</b> dropdown
            → <b>Medical Report</b>. The post-generation modal has Open / Print /
            Email tiles — Email pre-fills the patient's address and sends the PDF.
          </div>
          <button
            onClick={() => nav(backTo)}
            className="mt-2 text-xs font-bold bg-amber-600 hover:bg-amber-700 text-white rounded px-3 py-1.5"
          >
            Take me there
          </button>
        </div>
      )}
    </div>
  )
}

function ActionItem({ icon, label, hint, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-4 py-2.5 hover:bg-wantok-50 active:bg-wantok-100 inline-flex items-start gap-3 border-b border-slate-50 last:border-b-0"
    >
      <span className="text-xl leading-none mt-0.5">{icon}</span>
      <span className="flex-1">
        <span className="block text-sm font-bold text-slate-800">{label}</span>
        <span className="block text-[11px] text-slate-500 mt-0.5">{hint}</span>
      </span>
    </button>
  )
}
